import React, { useEffect } from "react";
import clsx from "clsx";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useDebouncedCallback } from "use-debounce";
import { useDispatch, useSelector } from "../../hooks";

import AlertContainer from "../../components/common/AlertContainer";

import { onAlertClose, handleFieldValueChange } from "./slices/register.slice";

import { SubmitButton } from "../../components/controls/SubmitButton";
import { trackEvent } from "../../utils/tracking";
import { TRACKING_EVENTS } from "../../utils/trackingDefinitions";
import SpinningCircle from "../../components/common/SpinningCircle";

import { ClubBenefits } from "../../components/common/ClubBenefits";

import { normalizeValue } from "../../utils/helpers";
import { CompanyConsents } from "./components/CompanyConsents";
import Alert from "../../components/common/Alert";
import { Email } from "../../components/controls/Email";
import { Password } from "../../components/controls/Password";
import { getProfileData, verifyProfile } from "./slices/register.actions";
import { PayloadAction } from "@reduxjs/toolkit";
import { TYPE_DEBOUNCE } from "../settings/common.utils";

export const VerifyProfileForm = () => {
	const dispatch = useDispatch();
	const { formatMessage } = useIntl();

	const profileDataReady = useSelector(
		(state) => state.registration.profile.profileDataReady,
	);

	const email = useSelector((state) => state.registration.profile.form.email);

	const id = useSelector((state) => state.registration.profile.form.id);

	const role = useSelector((state) => state.registration.profile.form.role);

	const isClubMember = useSelector(
		(state) => state.registration.profile.form.isClubMember,
	);

	const password = useSelector(
		(state) => state.registration.profile.form.password,
	);

	const alerts = useSelector((state) => state.registration.alerts);

	const returnUrl = useSelector(
		(state) => state.registration.b2b.form.returnUrl,
	);

	const isFetching = useSelector(
		(state) => state.registration.profile.isFetching,
	);

	const isBusinessConsentsEnabled = useSelector(
		(state) => state.settings.features.businessConsents,
	);

	const acceptSmsNotifications = useSelector(
		(state) => state.registration.profile.form.acceptSmsNotifications,
	);

	const acceptClubAgreementConsents = useSelector(
		(state) => state.registration.profile.form.acceptClubAgreementConsents,
	);

	const acceptOffersInformations = useSelector(
		(state) => state.registration.profile.form.acceptOffersInformations,
	);

	const validationRules = useSelector(
		(state) => state.settings.validationRules,
	);

	const schema = yup.object({
		email: yup
			.string()
			.test("valid-email", "komid.register.validation.required", (value) =>
				new RegExp(validationRules.email).test(value),
			)
			.min(2, "komid.register.validation.required")
			.max(70, "komid.register.validation.length.max")
			.required(),
		password: yup
			.string()
			.min(8, "komid.register.validation.required")
			.max(70, "komid.register.validation.length.max")
			.required(),
		acceptSmsNotifications: yup
			.bool()
			.test(
				"other-checked",
				"komid.register.business.consents.accept.sms.error.message",
				(value, context) => {
					if (value && !context.parent.acceptClubAgreementConsents) {
						return null;
					}
					return true;
				},
			),
		acceptClubAgreementConsents: yup
			.bool()
			.oneOf([true, false], "komid.register.validation.required"),
		acceptOffersInformations: yup
			.bool()
			.oneOf([true, false], "komid.register.validation.required"),
	});

	const formMethods = useForm({
		mode: "onChange",
		reValidateMode: "onChange",
		resolver: yupResolver(schema),
		defaultValues: {
			email,
			password,
			acceptSmsNotifications,
			acceptClubAgreementConsents,
			acceptOffersInformations,
		},
	});

	const {
		handleSubmit,
		clearErrors,
		setValue,
		trigger,
		register,
		formState: { errors },
	} = formMethods;

	const handleDebounedFieldChange = useDebouncedCallback(async (payload) => {
		const isValid = await trigger(payload.field);

		if (isValid) {
			clearErrors(payload.field);
		}

		dispatch(handleFieldValueChange(payload));
	}, TYPE_DEBOUNCE);

	const onFieldValueChange = async (event) => {
		const { id, value } = event.target;

		setValue(id, value);

		await handleDebounedFieldChange({
			form: "profile",
			field: id,
			value,
		});
	};

	const handleProfileVerification = async (data) => {
		dispatch(onAlertClose([]));

		let payload = {
			id,
			username: normalizeValue(data.email),
			role,
			password: normalizeValue(data.password),
			returnUrl,
			verify: data.verify,
		};

		let validationFields: (
			| "password"
			| "email"
			| "acceptSmsNotifications"
			| "acceptClubAgreementConsents"
			| "acceptOffersInformations"
		)[] = ["password", "email"];

		if (isBusinessConsentsEnabled) {
			Object.assign(payload, {
				consents: {
					clubAgreement: {
						consented: data.acceptClubAgreementConsents,
						consentText: formatMessage({
							id: "consents.clubAgreement.text",
						}),
					},
					clubSms: {
						consented: data.acceptSmsNotifications,
						consentText: formatMessage({
							id: "consents.clubSms.text",
						}),
					},
				},
			});

			validationFields.push(
				"acceptClubAgreementConsents",
				"acceptSmsNotifications",
			);
		} else {
			Object.assign(payload, {
				consents: {
					newsletterEmail: {
						consented: data.acceptOffersInformations,
						consentText: formatMessage({
							id: "consents.newsletter.text",
						}),
					},
				},
			});

			validationFields.push("acceptOffersInformations");
		}
		const isValid = await trigger(validationFields);

		if (isValid) {
			dispatch(verifyProfile(payload)).then(
				(
					response: PayloadAction<{
						error?: string;
						isSuccess?: boolean;
						returnUrl?: string;
					}>,
				) => {
					if (response.payload.isSuccess) {
						window.location.href = response.payload.returnUrl;
					}
				},
			);
		} else {
			Object.entries(errors).forEach(([field, _]) => {
				trackEvent({
					event: TRACKING_EVENTS.REGISTRATION.EVENT.VALIDATION_ERROR,
					target: TRACKING_EVENTS.REGISTRATION.APP,
					value: field,
				});
			});
		}
	};

	useEffect(() => {
		const fetchProfile = async () => {
			if (!profileDataReady) {
				const response = await dispatch(getProfileData(returnUrl));
				const profile = response.payload as {
					error?: string;
					isSuccess: boolean;
					result: { username: string; id: string; role: string };
				};

				if (profile.result?.username) {
					setValue("email", profile.result.username);
				}
			}
		};
		fetchProfile();
	}, [profileDataReady]);

	return (
		<>
			<AlertContainer
				alerts={alerts}
				onAlertClose={(data) => dispatch(onAlertClose(data))}
			/>
			{profileDataReady && (
				<div
					className={clsx({
						"form-wrapper": true,
						club: isBusinessConsentsEnabled,
					})}
				>
					{isBusinessConsentsEnabled && <ClubBenefits target="sidebar" />}
					<section key="registration" className="form-section">
						<form
							action=""
							autoComplete="off"
							onSubmit={handleSubmit((data) =>
								handleProfileVerification({ ...data, verify: true }),
							)}
						>
							<fieldset>
								<h1>
									{formatMessage({ id: "komid.register.verify.profile.title" })}
								</h1>
								<FormProvider {...formMethods}>
									<div className="form-notice">
										<Alert
											level="info"
											messageKey="komid.register.verify.profile.role"
											messageValues={{
												role,
											}}
										/>
									</div>

									<Email
										id="email"
										className="form-input"
										ref={register("email", { value: email })?.ref}
										error={
											typeof errors.email?.message === "string"
												? errors.email.message
												: undefined
										}
										value={email}
										disabled={true}
										autoComplete="new-password"
										autoFocus={false}
										label={formatMessage({
											id: "komid.register.verify.profile.your.email",
										})}
										placeholder={formatMessage({
											id: "komid.register.email.placeholder",
										})}
										onChange={(event) => {
											onFieldValueChange(event);
										}}
										onBlur={(event) => {
											if (email !== event.target.value) {
												onFieldValueChange(event);
											}
										}}
									/>

									<Password
										id="password"
										ref={register("password", { value: password })?.ref}
										error={
											typeof errors.password?.message === "string"
												? errors.password.message
												: undefined
										}
										className="form-input"
										label={formatMessage({
											id: "komid.register.verify.profile.your.password",
										})}
										onChange={(event) => {
											onFieldValueChange(event);
										}}
										onBlur={(event) => {
											if (password !== event.target.value) {
												onFieldValueChange(event);
											}
										}}
										placeholder="******"
										maxLength={50}
										autoComplete="new-password"
									/>

									<CompanyConsents
										hideSalesConditions={true}
										isClubMember={isClubMember}
										target="profile"
									/>
									<div className="form-buttons">
										<SubmitButton
											isFetching={isFetching}
											onClick={handleSubmit((data) =>
												handleProfileVerification({ ...data, verify: true }),
											)}
										>
											{formatMessage({
												id: "komid.register.verify.profile.continue.verification",
											})}
											{isFetching && <SpinningCircle className="loader" />}
										</SubmitButton>
									</div>
									<div className="form-bottom-links">
										<span>
											{formatMessage({ id: "komid.login.separatortext" })}
										</span>
										<a
											href="#"
											className="link"
											onClick={handleSubmit((data) =>
												handleProfileVerification({ ...data, verify: false }),
											)}
											data-testid="register.business.form.verify.profile.skip.verification.link"
										>
											{formatMessage({
												id: "komid.register.verify.profile.skip.verification",
											})}
										</a>
									</div>
								</FormProvider>
							</fieldset>
						</form>
					</section>
					{isBusinessConsentsEnabled && <div className="club-spacer" />}
				</div>
			)}
		</>
	);
};
